import { useContext, useMemo } from 'react';
import type { FunctionComponent, PropsWithChildren } from 'react';

import type {
  Action,
  Activity,
  APMAsset,
  APMClient,
  Checklist,
  ChecklistItem,
  ChecklistItemResponseType,
  ChecklistResponseType,
  Condition,
  ConditionalAction,
  FdmFile,
  ICDFView,
  Measurement,
  Notification,
  Operation,
  Options,
  ServiceContract,
  TaskIntervalUpsertType,
  TemplateResponseType,
  UserService,
} from '@cognite/apm-client';
import {
  createCDFView,
  createViewCache,
  createAPMAppDataService,
  createInFieldLocationConfigService,
  createTemplateItemsService,
  createUserPreferencesService,
} from '@cognite/apm-client';
import { Heading } from '@cognite/cogs-core-v11/tokens/typography/typography.tsx';
import { Loader } from '@cognite/cogs-core-v11/components/loader/loader.tsx';
import { useAuthContext } from '@cognite/e2e-auth';
import { createFDMClient } from '@cognite/fdm-client';
import type { IFDMClient } from '@cognite/fdm-client';
import type {
  CogniteClient,
  Timeseries,
  ViewDefinition,
  ViewReference,
} from '@cognite/sdk';
import { createTagService } from '@cognite/solution-tags';
import { useTranslation } from 'react-i18next';

import type { InfieldViewReferences } from '../../constants/cdm-infield-data-model';
import {
  CDM_INFIELD_SCHEMA_MODEL_EXTERNAL_ID,
  CDM_INFIELD_SCHEMA_MODEL_VERSION,
  CDM_INFIELD_SCHEMA_SPACE,
  INFIELD_USER_CONTRACT_PROPERTIES,
  INFIELD_USER_PREFERENCES_CONTRACT_PROPERTIES,
  SCHEDULER_DATA_MODEL_FLAG,
  USER_INSTANCE_SPACE,
  USER_MODEL_NAME,
  USER_MODEL_VERSION,
  USER_PREFERENCES_VIEW_NAME,
  USER_VIEW_NAME,
} from '../../constants/cdm-infield-data-model';
import { createCdmThreeDService } from '../../features/3d/cdm-3d-service';
import { createActivityIDMMigrator } from '../../features/activities/activity-idm-migrator';
import { createCdmActivityService } from '../../features/activities/cdm-activity-service';
import { createOperationIDMMigrator } from '../../features/activities/operation-idm-migrator';
import { AppConfigErrorScreen } from '../../features/app-config/app-config-error-screen/cdm-app-config-error-screen';
import type {
  DataExplorationConfig,
  InFieldCDMLocationConfig,
  ObservationViewConfig,
  ViewMappingReference,
} from '../../features/app-config/cdm-types';
import { defaultAppConfig } from '../../features/app-config/default-config';
import { toViewReference } from '../../features/app-config/utils/cdm-utils';
import { createAssetIDMMigrator } from '../../features/asset/asset-idm-migrator';
import { createCdmAssetService } from '../../features/asset/cdm-asset-service';
import { createCdmEdgeToDirectRelationService } from '../../features/cdm-relationship-service/cdm-edge-to-direct-relation-service';
import { createCdmChecklistService } from '../../features/checklist/cdm-checklist-service';
import type {
  CDMChecklistItemResponseType,
  CDMChecklistResponseType,
} from '../../features/checklist/cdm-types';
import { createCdmChecklistSchedulerService } from '../../features/checklist/checklist-scheduler/cdm-checklist-scheduler-service';
import { createActionsService } from '../../features/conditions/actions/actions-service';
import { createConditionalActionsService } from '../../features/conditions/conditional-actions/conditional-actions-service';
import { createConditionsService } from '../../features/conditions/conditions/conditions.service';
import { createIdmMediaService } from '../../features/idm-media/services/idm-media-service';
import { resolvePrimaryObservationViewConfig } from '../../features/idm-observations/observation-view-config-utils';
import { createIdmObservationsService } from '../../features/idm-observations/services/idm-observations-service';
import { createIdmSearchService } from '../../features/idm-search/services/idm-search-service';
import { createCdmMeasurementService } from '../../features/measurements/cdm-measurement-service';
import { createCdmMediaService } from '../../features/media/cdm-media-service';
import { createFileIDMMigrator } from '../../features/media/file-idm-migrator';
import { createNotificationIDMMigrator } from '../../features/notifications/notification-idm-migrator';
import { createNotificationService } from '../../features/notifications/notification-service';
import type { ObservationService } from '../../features/observation/observation-service';
import { createCdmIntervalService } from '../../features/task/interval-list/cdm-interval-service';
import type { ScheduleUpsertType } from '../../features/task/interval-list/cdm-types';
import { createCdmTemplateService } from '../../features/template/cdm-template-service';
import type {
  CdmTemplateItemResponseType,
  CdmTemplateResponseType,
} from '../../features/template/cdm-types';
import { createCdmTimeseriesService } from '../../features/timeseries/cdm-timeseries-service';
import { createTimeseriesIDMMigrator } from '../../features/timeseries/timeseries-idm-migrator';
import { createViewService } from '../../features/view/services/view-service';
import { useSaveCdmOfflineConfiguration } from '../offline/cdm-use-save-offline-configuration';
import { useSavedCdmConfiguration } from '../offline/cdm-use-saved-configuration';
import { useGetViewsByReferences } from '../hooks/cdm-use-get-views-by-references';
import { useCdmSelectedLocationContext } from '../selected-location-provider/cdm-selected-location-context';

import {
  CdmFDMServicesProviderContext,
  type CdmFDMServicesProviderContextInterface,
  type MigratorFactories,
  type ServiceFactories,
} from './cdm-fdm-services-provider.context';
import {
  convertViewReferencesToViewVersions,
  getCdmViewReferencesFromModelVersion,
  getRequiredAssetViewReferences,
  isViewReferenceMatch,
} from './cdm-view-reference-utils';
import { FDMServicesContext } from './fdm-services.context';

const defaultServiceFactories: ServiceFactories = {
  createCDFView,
  createViewCache,
  createAPMAppDataService,
  createInFieldLocationConfigService,
  createTemplateItemsService,
  createUserPreferencesService,
  createFDMClient,
  createTagService,
  createCdmThreeDService,
  createCdmActivityService,
  createCdmAssetService,
  createCdmChecklistService,
  createCdmChecklistSchedulerService,
  createActionsService,
  createConditionalActionsService,
  createConditionsService,
  createIdmMediaService,
  createIdmObservationsService,
  createIdmSearchService,
  createViewService,
  createCdmMeasurementService,
  createCdmMediaService,
  createNotificationService,
  createCdmIntervalService,
  createCdmTemplateService,
  createCdmTimeseriesService,
  createCdmEdgeToDirectRelationService,
};

const defaultMigratorFactories: MigratorFactories = {
  createActivityIDMMigrator,
  createOperationIDMMigrator,
  createAssetIDMMigrator,
  createNotificationIDMMigrator,
  createTimeseriesIDMMigrator,
  createFileIDMMigrator,
};

export const defaultCdmFDMServicesProviderContextValue: CdmFDMServicesProviderContextInterface =
  {
    useCdmSelectedLocationContext,
    useGetViewsByReferences: (references, enabled = true) =>
      useSavedCdmConfiguration({
        useOnlineQuery: ({ enabled: fetchEnabled }) =>
          useGetViewsByReferences(references, fetchEnabled),
        enabled: enabled && references.length > 0,
        select: (configuration) =>
          references.map((reference) => {
            const view = configuration.assetViews.find((candidate) =>
              isViewReferenceMatch(candidate, reference)
            );
            if (!view)
              throw new Error(
                `Requested asset view is not saved: ${reference.space}/${reference.externalId}/${reference.version}`
              );
            return view;
          }),
      }),
    useSaveCdmOfflineConfiguration,
    useAuthContext,
    serviceFactories: defaultServiceFactories,
    migratorFactories: defaultMigratorFactories,
    AppConfigErrorScreen,
  };

export const CdmFDMServicesProvider: FunctionComponent<PropsWithChildren> = ({
  children,
}) => {
  const {
    useCdmSelectedLocationContext,
    useGetViewsByReferences,
    useSaveCdmOfflineConfiguration,
    useAuthContext,
    serviceFactories,
    migratorFactories,
    AppConfigErrorScreen,
  } =
    useContext(CdmFDMServicesProviderContext) ??
    defaultCdmFDMServicesProviderContextValue;

  const { t } = useTranslation('activity');

  const { client: cogniteClient } = useAuthContext();
  const { selectedLocationConfig } = useCdmSelectedLocationContext();
  const appDataModelVersion = CDM_INFIELD_SCHEMA_MODEL_VERSION;
  const appDataModelSpace = CDM_INFIELD_SCHEMA_SPACE;
  const appDataModelExternalId = CDM_INFIELD_SCHEMA_MODEL_EXTERNAL_ID;
  const baseViewReferences = useMemo(
    () => getCdmViewReferencesFromModelVersion(appDataModelVersion),
    [appDataModelVersion]
  );
  const {
    maintenanceOrderView,
    operationsView,
    notificationsView,
    checklistView,
    checklistItemView,
    assetsView,
    filesView,
    timeseriesView,
    observationViewConfig,
    assetPropertiesCardConfig,
    assetActivitiesCardView,
    assetNotificationsCardView,
    assetDocumentsCardView,
  } = useViews(
    selectedLocationConfig?.viewMappings,
    baseViewReferences,
    selectedLocationConfig?.dataExplorationConfig
  );

  // This is used in the asset idm migrator to map all extra/custom properties to the metadata field of the classic asset type
  const viewsToFetch = useMemo(
    () => getRequiredAssetViewReferences(assetsView),
    [assetsView]
  );

  const { data: assetViews, isLoading: isLoadingAssetViews } =
    useGetViewsByReferences(viewsToFetch, Boolean(selectedLocationConfig));
  const [baseAssetView, customAssetView] = assetViews ?? [];

  useSaveCdmOfflineConfiguration({
    assetViewReferences: viewsToFetch,
    assetViews,
  });

  const fdmClient = useMemo(
    () => serviceFactories.createFDMClient(cogniteClient),
    [cogniteClient, serviceFactories]
  );

  const {
    appServiceContract,
    userServiceContract,
    inFieldSpecificConfigServiceContract,
  } = useServiceContracts(
    selectedLocationConfig,
    fdmClient,
    appDataModelSpace,
    appDataModelExternalId,
    appDataModelVersion
  );

  const apmClient = useMemo(
    () =>
      createApmClient({
        appDataModelSpace,
        appDataModelVersion,
        cogniteClient,
        fdmClient,
        selectedLocationConfig,
        serviceContracts: {
          appServiceContract,
          inFieldSpecificConfigServiceContract,
          userServiceContract,
        },
        userInstanceSpace: USER_INSTANCE_SPACE,
        viewDefinitions: {
          baseAssetView,
          customAssetView,
        },
        viewReferences: {
          maintenanceOrderView,
          assetsView,
          checklistView,
          filesView,
          notificationsView,
          operationsView,
          timeseriesView,
        },
        serviceFactories,
        migratorFactories,
        baseViewReferences,
        appDataModelExternalId,
      }),
    [
      appDataModelSpace,
      appDataModelVersion,
      cogniteClient,
      fdmClient,
      selectedLocationConfig,
      appServiceContract,
      inFieldSpecificConfigServiceContract,
      userServiceContract,
      baseAssetView,
      customAssetView,
      checklistView,
      maintenanceOrderView,
      assetsView,
      filesView,
      notificationsView,
      operationsView,
      timeseriesView,
      serviceFactories,
      migratorFactories,
      baseViewReferences,
      appDataModelExternalId,
    ]
  );

  const fdmServices = useMemo(() => {
    // TODO(FO-3090): Investigate if we can remove the isLoadingAssetViews check
    if (!isLoadingAssetViews && baseAssetView) {
      return createFdmServices({
        apmClient,
        cogniteClient,
        userInstanceSpace: USER_INSTANCE_SPACE,
        serviceFactories,
        selectedLocationConfig,
        baseViewReferences,
        appServiceContract,
        appDataModelSpace,
        appDataModelExternalId,
        appDataModelVersion,
        observationViewConfig,
        assetsView,
        assetPropertiesCardConfig,
        assetActivitiesCardView,
        assetNotificationsCardView,
        assetDocumentsCardView,
        timeseriesView,
        filesView,
        checklistItemView,
      });
    }
  }, [
    isLoadingAssetViews,
    baseAssetView,
    apmClient,
    cogniteClient,
    serviceFactories,
    selectedLocationConfig,
    baseViewReferences,
    appServiceContract,
    appDataModelSpace,
    appDataModelExternalId,
    appDataModelVersion,
    observationViewConfig,
    assetsView,
    assetPropertiesCardConfig,
    assetActivitiesCardView,
    assetNotificationsCardView,
    assetDocumentsCardView,
    timeseriesView,
    filesView,
    checklistItemView,
  ]);

  if (fdmServices) {
    return (
      <FDMServicesContext.Provider value={fdmServices}>
        {children}
      </FDMServicesContext.Provider>
    );
  }

  if (isLoadingAssetViews) {
    return (
      <Loader
        infoText={
          <Heading level={6}>
            {t(
              'SERVICE_PROVIDER_LOADING_APP_CONFIGURATION',
              'Loading app configuration'
            )}
          </Heading>
        }
      />
    );
  }

  return (
    <AppConfigErrorScreen
      title={t(
        'errorLoadingAppConfigurationTitle',
        'Failed to load app configuration'
      )}
      body={t(
        'errorLoadingAppConfigurationDescription',
        'An error occurred while loading app configuration. Please try again or contact support.'
      )}
    />
  );
};

// core model has timeseries and files mark as optional, cdm model has them required
export type RequiredAPMClient = Required<APMClient>;

/**
 * Private factory for creating the apm client
 *
 * NOTE(eiriklv): It is debatable that this should be pulled out
 * to a separate file and tested, but you could also argue that
 * since it's only consumed (and should only be consumed) by this
 * file then it shouldn't be made public.
 *
 * This function will be tested transiently through the `CdmFDMServicesProvider`
 */
const createApmClient = ({
  cogniteClient,
  fdmClient,
  selectedLocationConfig,
  userInstanceSpace,
  appDataModelSpace,
  appDataModelVersion,
  viewReferences,
  viewDefinitions,
  serviceContracts,
  serviceFactories,
  migratorFactories,
  baseViewReferences,
  appDataModelExternalId,
}: {
  cogniteClient: CogniteClient;
  fdmClient: IFDMClient;
  selectedLocationConfig?: InFieldCDMLocationConfig;
  userInstanceSpace: string;
  appDataModelSpace: string;
  appDataModelVersion: string;
  viewReferences: {
    maintenanceOrderView: Omit<ViewReference, 'type'>;
    operationsView: Omit<ViewReference, 'type'>;
    checklistView: Omit<ViewReference, 'type'>;
    notificationsView: Omit<ViewReference, 'type'>;
    assetsView: Omit<ViewReference, 'type'>;
    filesView: Omit<ViewReference, 'type'>;
    timeseriesView: Omit<ViewReference, 'type'>;
  };
  viewDefinitions: {
    baseAssetView?: ViewDefinition; // This is used in the asset idm migrator to map all extra/custom properties to the metadata field of the classic asset type
    customAssetView?: ViewDefinition;
  };
  serviceContracts: {
    inFieldSpecificConfigServiceContract: ServiceContract;
    userServiceContract: ServiceContract;
    appServiceContract: ServiceContract;
  };
  serviceFactories: ServiceFactories;
  migratorFactories: MigratorFactories;
  baseViewReferences: InfieldViewReferences;
  appDataModelExternalId: string;
}): RequiredAPMClient => {
  const resolvedAppDataInstanceSpace =
    selectedLocationConfig?.dataStorage?.appInstanceSpace ||
    defaultAppConfig.appDataSpaceId!;

  const apmClientOptions: Options = {
    userViewName: USER_VIEW_NAME,
    userModelSpace: USER_VIEW_NAME,
    userModelName: USER_MODEL_NAME,
    userInstanceSpace,
    userModelVersion: USER_MODEL_VERSION,
    userPreferencesViewName: USER_PREFERENCES_VIEW_NAME,
    activitiesView: viewReferences.maintenanceOrderView,
    operationsView: viewReferences.operationsView,
    notificationsView: viewReferences.notificationsView,
    assetsView: viewReferences.assetsView,
    filesView: viewReferences.filesView,
    timeseriesView: viewReferences.timeseriesView,
  };

  const overriddenApmClient = {
    fdmClient,
    appDataModelSpace: appDataModelSpace,
    appDataModelName: appDataModelExternalId,
    appDataModelVersion: appDataModelVersion,
    appDataInstanceSpace: resolvedAppDataInstanceSpace,
    sourceDataModelSpace:
      selectedLocationConfig?.dataFilters?.assets?.instanceSpaces?.[0] ??
      defaultAppConfig.customerDataSpaceId!,
    sourceDataModelName:
      selectedLocationConfig?.dataFilters?.assets?.instanceSpaces?.[0] ??
      defaultAppConfig.customerDataSpaceId!,
    sourceDataModelVersion: defaultAppConfig.customerDataSpaceVersion!,
    sourceDataInstanceSpace:
      selectedLocationConfig?.dataFilters?.assets?.instanceSpaces?.[0] ??
      defaultAppConfig.customerDataSpaceId!,
    userInstanceSpace,
    options: apmClientOptions,
    solutionTags: serviceFactories.createTagService(
      cogniteClient,
      resolvedAppDataInstanceSpace
    ),
    /**
     * @deprecated This service is deprecated. Use SelectedLocationContext to get all locations instead.
     * This should be removed - investigate how this can be done.
     */
    infieldLocationConfig: serviceFactories.createInFieldLocationConfigService(
      serviceContracts.inFieldSpecificConfigServiceContract
    ),
    viewCache: serviceFactories.createViewCache(cogniteClient),
    /**
     * @deprecated This object is deprecated. Use getViewReference methods from services instead.
     */
    viewVersions: convertViewReferencesToViewVersions(baseViewReferences),
    /**
     * NOTE(eiriklv): None of this should be consumed by the application,
     * as we're using the new idm observations service. It's only here
     * to conform to the interface of the context value
     */
    /**
     * @deprecated This service is deprecated. Use idmObservationsService instead.
     */
    observations: {} as APMClient['observations'],
    /**
     * @deprecated This service is deprecated. Use idmObservationsService instead.
     */
    observationConfig: {} as APMClient['observationConfig'],
    /**
     * @deprecated This service is deprecated. Use idmObservationsService instead.
     */
    observationsDMS: {} as APMClient['observationsDMS'],
    /**
     * @deprecated This service is deprecated. Use useUserServices to get the user service instead.
     */
    userPreferences: serviceFactories.createUserPreferencesService(
      serviceContracts.userServiceContract,
      {
        userPreferencesViewName:
          baseViewReferences.infieldUserPreferences.externalId,
      }
    ),
    /**
     * @deprecated This service is deprecated. Use useUserServices hook to get the user service instead.
     */
    users: {} as UserService,
    notifications: serviceFactories.createCDFView<Notification>(
      cogniteClient,
      viewReferences.notificationsView,
      selectedLocationConfig?.dataFilters?.notifications?.instanceSpaces?.[0] ??
        'notifications_space_not_set',
      undefined,
      {
        migrator: migratorFactories.createNotificationIDMMigrator(
          selectedLocationConfig?.dataFilters?.notifications
            ?.instanceSpaces?.[0] ?? 'notifications_space_not_set',
          selectedLocationConfig?.dataFilters?.assets?.instanceSpaces?.[0]
        ),
      }
    ),
    /**
     * @deprecated This service is deprecated. Use checklistService from fdmServices instead.
     */
    checklists: serviceFactories.createAPMAppDataService<ChecklistResponseType>(
      serviceContracts.appServiceContract,
      baseViewReferences.infieldChecklist.externalId,
      baseViewReferences.infieldChecklist.version,
      {
        ignoreArchivedByDefault: true,
      }
    ),
    /**
     * @deprecated This service is deprecated. Use checklistItemsService from fdmServices instead.
     */
    checklistItems:
      serviceFactories.createAPMAppDataService<ChecklistItemResponseType>(
        serviceContracts.appServiceContract,
        baseViewReferences.infieldChecklistItem.externalId,
        baseViewReferences.infieldChecklistItem.version,
        {
          ignoreArchivedByDefault: true,
        }
      ),
    /**
     * @deprecated This service is deprecated. Use checklistService from fdmServices instead.
     */
    checklistsDMS: serviceFactories.createCDFView<Checklist>(
      cogniteClient,
      viewReferences.checklistView,
      selectedLocationConfig?.dataStorage?.appInstanceSpace ||
        'app_instance_space_not_set',
      undefined
    ),

    /**
     * TODO(FO-2741): Multiple spaces for files support
     */
    files: serviceFactories.createCDFView<FdmFile>(
      cogniteClient,
      viewReferences.filesView,
      selectedLocationConfig?.dataStorage?.appInstanceSpace ||
        'app_instance_space_not_set',
      undefined,
      {
        migrator: migratorFactories.createFileIDMMigrator(
          selectedLocationConfig?.dataStorage?.appInstanceSpace ||
            'app_instance_space_not_set'
        ),
      }
    ),

    activities: serviceFactories.createCDFView<Activity>(
      cogniteClient,
      viewReferences.maintenanceOrderView,
      selectedLocationConfig?.dataFilters?.maintenanceOrders
        ?.instanceSpaces?.[0] ?? 'maintenance_orders_space_not_set',
      undefined,
      {
        migrator: migratorFactories.createActivityIDMMigrator(
          selectedLocationConfig?.dataFilters?.maintenanceOrders
            ?.instanceSpaces?.[0] ?? 'maintenance_orders_space_not_set',
          selectedLocationConfig?.dataFilters?.assets?.instanceSpaces?.[0]
        ),
      }
    ),
    operations: serviceFactories.createCDFView<Operation>(
      cogniteClient,
      viewReferences.operationsView,
      selectedLocationConfig?.dataFilters?.operations?.instanceSpaces?.[0] ||
        'operations_space_not_set',
      undefined,
      {
        migrator: migratorFactories.createOperationIDMMigrator(
          selectedLocationConfig?.dataFilters?.operations
            ?.instanceSpaces?.[0] ?? 'operations_space_not_set'
        ),
      }
    ),
    timeseries: serviceFactories.createCDFView<Timeseries>(
      cogniteClient,
      viewReferences.timeseriesView,
      selectedLocationConfig?.dataFilters?.timeseries?.instanceSpaces?.[0] ??
        'time_series_space_not_set',
      undefined,
      {
        migrator: migratorFactories.createTimeseriesIDMMigrator(),
      }
    ),
    assets: serviceFactories.createCDFView<APMAsset>(
      cogniteClient,
      viewReferences.assetsView,
      selectedLocationConfig?.dataFilters?.assets?.instanceSpaces?.[0] ??
        'assets_space_not_set',
      undefined,
      {
        migrator: viewDefinitions.baseAssetView
          ? migratorFactories.createAssetIDMMigrator(
              selectedLocationConfig?.dataFilters?.assets
                ?.instanceSpaces?.[0] ?? 'assets_space_not_set',
              viewDefinitions.baseAssetView,
              viewDefinitions.customAssetView || viewDefinitions.baseAssetView
            )
          : undefined,
      }
    ),
    measurements: serviceFactories.createAPMAppDataService<Measurement>(
      serviceContracts.appServiceContract,
      baseViewReferences.infieldMeasurementReading.externalId,
      baseViewReferences.infieldMeasurementReading.version,
      {
        ignoreArchivedByDefault: true,
      }
    ),
    conditions: serviceFactories.createAPMAppDataService<Condition>(
      serviceContracts.appServiceContract,
      baseViewReferences.infieldCondition.externalId,
      baseViewReferences.infieldCondition.version,
      {
        ignoreArchivedByDefault: true,
      }
    ),
    conditionalActions:
      serviceFactories.createAPMAppDataService<ConditionalAction>(
        serviceContracts.appServiceContract,
        baseViewReferences.infieldConditionalAction.externalId,
        baseViewReferences.infieldConditionalAction.version,
        {
          ignoreArchivedByDefault: true,
        }
      ),
    actions: serviceFactories.createAPMAppDataService<Action>(
      serviceContracts.appServiceContract,
      baseViewReferences.infieldAction.externalId,
      baseViewReferences.infieldAction.version,
      {
        ignoreArchivedByDefault: true,
      }
    ),
    // TODO(FO-3087): When useTemplatesSearch is updated to not use this service directly the empty object {} should be returned
    /**
     * @deprecated This service is deprecated. Use templateService from fdmServices instead. Currently used in useTemplatesSearch where the DMS search is used.
     */
    templates: serviceFactories.createAPMAppDataService<TemplateResponseType>(
      serviceContracts.appServiceContract,
      baseViewReferences.infieldTemplate.externalId,
      baseViewReferences.infieldTemplate.version,
      {
        ignoreArchivedByDefault: true,
      }
    ),
    /**
     * @deprecated This service is deprecated. Use templateService from fdmServices instead.
     */
    templateItems: {} as APMClient['templateItems'],
    intervals: serviceFactories.createAPMAppDataService<TaskIntervalUpsertType>(
      serviceContracts.appServiceContract,
      baseViewReferences.infieldSchedule.externalId,
      baseViewReferences.infieldSchedule.version,
      {
        ignoreArchivedByDefault: true,
      }
    ),
    edgeService: serviceFactories.createCdmEdgeToDirectRelationService(
      serviceContracts.appServiceContract,
      baseViewReferences,
      cogniteClient
    ),
    isModelVersionV12OrAbove(): boolean {
      return true;
    },
    supportsSolutionTags(): boolean {
      return true;
    },
  };

  return overriddenApmClient;
};

/**
 * Private factory for creating the fdm services
 *
 * NOTE(eiriklv): It is debatable that this should be pulled out
 * to a separate file and tested, but you could also argue that
 * since it's only consumed (and should only be consumed) by this
 * file then it shouldn't be made public.
 *
 * This function will be tested transiently through the `CdmFDMServicesProvider`
 */
const createFdmServices = ({
  cogniteClient,
  apmClient,
  selectedLocationConfig,
  userInstanceSpace,
  serviceFactories,
  baseViewReferences,
  appServiceContract,
  appDataModelSpace,
  appDataModelExternalId,
  appDataModelVersion,
  observationViewConfig,
  assetsView,
  assetPropertiesCardConfig,
  assetActivitiesCardView,
  assetNotificationsCardView,
  assetDocumentsCardView,
  timeseriesView,
  filesView,
  checklistItemView,
}: {
  cogniteClient: CogniteClient;
  apmClient: RequiredAPMClient;
  selectedLocationConfig?: InFieldCDMLocationConfig;
  userInstanceSpace: string;
  serviceFactories: ServiceFactories;
  baseViewReferences: InfieldViewReferences;
  appServiceContract: ServiceContract;
  appDataModelSpace: string;
  appDataModelExternalId: string;
  appDataModelVersion: string;
  observationViewConfig?: ObservationViewConfig;
  assetsView: ViewMappingReference;
  assetPropertiesCardConfig?: DataExplorationConfig['assetPropertiesCardConfig'];
  assetActivitiesCardView: ViewMappingReference;
  assetNotificationsCardView: ViewMappingReference;
  assetDocumentsCardView: ViewMappingReference;
  timeseriesView: ViewMappingReference;
  filesView: ViewMappingReference;
  checklistItemView: Omit<ViewReference, 'type'>;
}) => {
  const viewService = serviceFactories.createViewService(cogniteClient);
  const appInstanceSpace =
    selectedLocationConfig?.dataStorage?.appInstanceSpace ||
    'app_instance_space_not_set';
  const cogniteFileView = isViewReferenceMatch(
    filesView,
    baseViewReferences.cdmFile
  )
    ? undefined
    : serviceFactories.createCDFView<FdmFile>(
        cogniteClient,
        baseViewReferences.cdmFile,
        appInstanceSpace,
        undefined
      );
  return {
    activityService: serviceFactories.createCdmActivityService(
      cogniteClient,
      {
        activities: apmClient.activities,
        operations: apmClient.operations,
      },
      selectedLocationConfig?.dataFilters?.maintenanceOrders
        ?.instanceSpaces?.[0],
      selectedLocationConfig?.dataFilters?.assets?.instanceSpaces?.[0],
      toViewReference(assetActivitiesCardView)
    ),
    assetService: serviceFactories.createCdmAssetService(
      cogniteClient,
      { assets: apmClient.assets },
      selectedLocationConfig?.dataFilters?.assets?.instanceSpaces?.[0] ??
        'assets_space_not_set',
      toViewReference(assetsView),
      selectedLocationConfig?.dataStorage?.rootLocation,
      assetPropertiesCardConfig
    ),
    notificationService: serviceFactories.createNotificationService(
      {
        notifications: apmClient.notifications,
      },
      selectedLocationConfig?.dataFilters?.notifications?.instanceSpaces?.[0],
      selectedLocationConfig?.dataFilters?.assets?.instanceSpaces?.[0],
      toViewReference(assetNotificationsCardView)
    ),
    mediaService: serviceFactories.createCdmMediaService(
      cogniteClient,
      apmClient,
      selectedLocationConfig?.dataStorage?.appInstanceSpace ??
        'app_instance_space_not_set',
      selectedLocationConfig?.dataFilters?.files?.instanceSpaces ?? [],
      selectedLocationConfig?.dataFilters?.assets?.instanceSpaces?.[0] ??
        'asset_space_not_set',
      undefined,
      undefined,
      toViewReference(assetDocumentsCardView),
      cogniteFileView
    ),
    timeseriesService: serviceFactories.createCdmTimeseriesService(
      cogniteClient,
      {
        timeseries: apmClient.timeseries,
        assets: apmClient.assets,
      },
      selectedLocationConfig?.dataFilters?.timeseries?.instanceSpaces ?? [],
      selectedLocationConfig?.dataStorage?.appInstanceSpace ??
        'app_instance_space_not_set',
      toViewReference(timeseriesView),
      selectedLocationConfig?.dataStorage?.rootLocation
    ),
    threeDService: serviceFactories.createCdmThreeDService(
      cogniteClient,
      {
        assets: apmClient.assets,
      },
      baseViewReferences
    ),
    measurementsService: serviceFactories.createCdmMeasurementService(
      cogniteClient,
      {
        measurements: apmClient.measurements,
        userInstanceSpace,
        appDataInstanceSpace:
          selectedLocationConfig?.dataStorage?.appInstanceSpace ??
          'app_instance_space_not_set',
      }
    ),
    conditionsService: serviceFactories.createConditionsService(cogniteClient, {
      conditions: apmClient.conditions,
      appDataModelSpace: appDataModelSpace,
      userInstanceSpace,
      viewVersions: convertViewReferencesToViewVersions(baseViewReferences),
    }),
    conditionalActionsService: serviceFactories.createConditionalActionsService(
      cogniteClient,
      {
        conditionalActions: apmClient.conditionalActions,
        appDataModelSpace: appDataModelSpace,
        appDataInstanceSpace:
          selectedLocationConfig?.dataStorage?.appInstanceSpace ||
          'app_instance_space_not_set',
        userInstanceSpace,
        viewVersions: convertViewReferencesToViewVersions(baseViewReferences),
      }
    ),
    actionsService: serviceFactories.createActionsService(cogniteClient, {
      actions: apmClient.actions,
      userInstanceSpace,
    }),
    templateService: serviceFactories.createCdmTemplateService(
      cogniteClient,
      {
        templates:
          serviceFactories.createAPMAppDataService<CdmTemplateResponseType>(
            appServiceContract,
            baseViewReferences.infieldTemplate.externalId,
            baseViewReferences.infieldTemplate.version,
            {
              ignoreArchivedByDefault: true,
            }
          ),
        templateItems:
          serviceFactories.createAPMAppDataService<CdmTemplateItemResponseType>(
            appServiceContract,
            baseViewReferences.infieldTemplateItem.externalId,
            baseViewReferences.infieldTemplateItem.version,
            {
              ignoreArchivedByDefault: true,
            }
          ),
        appDataModelSpace: appDataModelSpace,
        appDataModelName: appDataModelExternalId,
        appDataModelVersion: appDataModelVersion,
        appDataInstanceSpace:
          selectedLocationConfig?.dataStorage?.appInstanceSpace ??
          'app_instance_space_not_set',
        userInstanceSpace,
        supportsSolutionTags: () => true,
      },
      selectedLocationConfig?.dataStorage?.rootLocation
    ),
    checklistService: serviceFactories.createCdmChecklistService(
      cogniteClient,
      {
        checklists:
          serviceFactories.createAPMAppDataService<CDMChecklistResponseType>(
            appServiceContract,
            baseViewReferences.infieldChecklist.externalId,
            baseViewReferences.infieldChecklist.version,
            {
              ignoreArchivedByDefault: true,
            }
          ),
        checklistItems:
          serviceFactories.createAPMAppDataService<CDMChecklistItemResponseType>(
            appServiceContract,
            baseViewReferences.infieldChecklistItem.externalId,
            baseViewReferences.infieldChecklistItem.version,
            {
              ignoreArchivedByDefault: true,
            }
          ),
        checklistsDMS: apmClient.checklistsDMS,
        appDataModelSpace: appDataModelSpace,
        appDataModelName: appDataModelExternalId,
        appDataModelVersion: appDataModelVersion,
        appDataInstanceSpace:
          selectedLocationConfig?.dataStorage?.appInstanceSpace ??
          'app_instance_space_not_set',
        userInstanceSpace,
        supportsSolutionTags: apmClient.supportsSolutionTags,
      },
      serviceFactories.createCDFView<ChecklistItem>(
        cogniteClient,
        checklistItemView,
        selectedLocationConfig?.dataStorage?.appInstanceSpace ||
          'app_instance_space_not_set',
        undefined
      ),
      selectedLocationConfig?.dataStorage?.rootLocation
    ),
    intervalService: serviceFactories.createCdmIntervalService(cogniteClient, {
      intervals: serviceFactories.createAPMAppDataService<ScheduleUpsertType>(
        appServiceContract,
        baseViewReferences.infieldSchedule.externalId,
        baseViewReferences.infieldSchedule.version
      ),
      userInstanceSpace,
    }),
    /**
     * NOTE(eiriklv): Ideally we would remove this,
     * but unfortunately it is being used directly
     * multiple places in the application
     */
    /**
     * @deprecated This service is deprecated. Use service from fdmServices directly instead.
     */
    apmClient: apmClient,
    /**
     * NOTE(eiriklv): This won't be used anymore,
     * as we'll use the new observations service for cdm/idm.
     */
    /**
     * @deprecated This service is deprecated. Use idmObservationsService instead.
     */
    observationService: {} as ObservationService,
    checklistSchedulerService:
      serviceFactories.createCdmChecklistSchedulerService(
        cogniteClient,
        // TODO(3176): In the future, it will make sense to pass in the data model itself here and pass that to the API,
        // but this is not supported yet
        SCHEDULER_DATA_MODEL_FLAG
      ),
    edgeService: serviceFactories.createCdmEdgeToDirectRelationService(
      appServiceContract,
      baseViewReferences,
      cogniteClient
    ),
    viewService,
    idmObservationService: serviceFactories.createIdmObservationsService({
      client: cogniteClient,
      viewService,
      viewConfig: observationViewConfig,
    }),
    idmSearchService: serviceFactories.createIdmSearchService(cogniteClient),
    // TODO(FO-3064): Combine media service and idmMediaService into one service
    idmMediaService: serviceFactories.createIdmMediaService(
      cogniteClient,
      undefined,
      { cdmFile: toViewReference(filesView) }
    ),
  };
};

const useViews = (
  viewMappings: InFieldCDMLocationConfig['viewMappings'],
  viewReferences: InfieldViewReferences,
  dataExplorationConfig: InFieldCDMLocationConfig['dataExplorationConfig']
) => {
  const maintenanceOrderView =
    viewMappings?.maintenanceOrder ?? viewReferences.idmMaintenanceOrder;

  const assetsView = viewMappings?.asset ?? viewReferences.infieldAsset;

  const operationsView = viewMappings?.operation ?? viewReferences.idmOperation;

  const notificationsView =
    viewMappings?.notification ?? viewReferences.idmNotification;

  const filesView = viewMappings?.file ?? viewReferences.cdmFile;

  const timeseriesView =
    viewMappings?.timeseries ?? viewReferences.infieldTimeseries;

  const checklistView = viewReferences.infieldChecklist;
  const checklistItemView = viewReferences.infieldChecklistItem;

  const observationViewConfig = useMemo(
    () => resolvePrimaryObservationViewConfig(viewMappings?.observation),
    [viewMappings?.observation]
  );

  const assetPropertiesCardConfig =
    dataExplorationConfig?.assetPropertiesCardConfig;

  const assetActivitiesCardView =
    dataExplorationConfig?.assetActivitiesCardView ?? maintenanceOrderView;

  const assetNotificationsCardView =
    dataExplorationConfig?.assetNotificationsCardView ?? notificationsView;

  const assetDocumentsCardView =
    dataExplorationConfig?.assetDocumentsCardView ?? filesView;

  return {
    maintenanceOrderView,
    operationsView,
    notificationsView,
    assetsView,
    checklistView,
    checklistItemView,
    filesView,
    timeseriesView,
    observationViewConfig,
    assetPropertiesCardConfig,
    assetActivitiesCardView,
    assetNotificationsCardView,
    assetDocumentsCardView,
  };
};

const useServiceContracts = (
  selectedLocationConfig: InFieldCDMLocationConfig | undefined,
  fdmClient: IFDMClient,
  appDataModelSpace: string,
  appDataModelExternalId: string,
  appDataModelVersion: string
) => {
  const appServiceContract: ServiceContract = useMemo(
    () => ({
      modelSpace: appDataModelSpace,
      modelName: appDataModelExternalId,
      modelVersion: appDataModelVersion,
      instanceSpace:
        selectedLocationConfig?.dataStorage?.appInstanceSpace ??
        'app_instance_space_not_set',
      fdmClient: fdmClient,
    }),
    [
      appDataModelSpace,
      appDataModelExternalId,
      appDataModelVersion,
      selectedLocationConfig?.dataStorage?.appInstanceSpace,
      fdmClient,
    ]
  );

  const userServiceContract: ServiceContract = useMemo(
    () => ({
      ...INFIELD_USER_CONTRACT_PROPERTIES,
      fdmClient: fdmClient,
    }),
    [fdmClient]
  );

  const inFieldSpecificConfigServiceContract: ServiceContract = useMemo(
    () => ({
      ...INFIELD_USER_PREFERENCES_CONTRACT_PROPERTIES,
      fdmClient: fdmClient,
    }),
    [fdmClient]
  );

  return {
    appServiceContract,
    userServiceContract,
    inFieldSpecificConfigServiceContract,
  };
};
